import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Button, AddCircleIcon } from '@cherre-frontend/ui';
import { RelayEnvironmentScope } from '@cherre-frontend/data-fetching';
import ModelLibrarySection from './ModelLibrarySection';
import { unifiedIngestListTransformTemplates } from '../services/dsp/unifiedIngestListTemplates';
import { unifiedIngestListTemplatesQuery } from '../services/dsp/__generated__/unifiedIngestListTemplatesQuery.graphql';
import { useUnifiedIngestDuplicateTemplates } from '../services/dsp/useUnifiedIngestDuplicateTemplates';
import { useSelector } from 'react-redux';
import { useModelTableDetails } from '../services/dom/useModelTableDetails';
import { useParams } from 'react-router';
import { fetchQuery, RelayEnvironmentProvider } from 'react-relay';
import { useDspRelayEnvironment } from 'src/products/connector/pages/connector-details/components/DataValidationV2/hooks/useDspRelayEnvironment';
import { useUnifiedIngestEditTemplates } from '../services/dsp/useUnifiedIngestEditTemplates';
import { useAppContext } from '@cherre-frontend/core';
export interface OrganizationOption {
  label: string;
  value: string;
}
type ModelLibrarySectionProps = {
  targetTableName?: string;
  handleCreateClick?: () => void;
};
export const TemplatesSection: React.FC<ModelLibrarySectionProps> = ({
  targetTableName,
  handleCreateClick,
}) => {
  const context = useAppContext();
  const { domOwner, organizationSlug, id } = useSelector(
    (state) => state.user.profile.value
  );

  const duplicateTemplate = useUnifiedIngestDuplicateTemplates();
  const editTemplate = useUnifiedIngestEditTemplates();
  const environment = useDspRelayEnvironment();
  const [templateList, setTemplateList] =
    useState<unifiedIngestListTemplatesQuery['response']>();
  const [tableTemplateList, setTableTemplateList] =
    useState<unifiedIngestListTemplatesQuery['response']>();
  const getTemplateList = useCallback(
    async (
      variables: unifiedIngestListTemplatesQuery['variables'],
      setTemplateList: React.Dispatch<
        React.SetStateAction<
          unifiedIngestListTemplatesQuery['response'] | undefined
        >
      >
    ) => {
      if (!environment) {
        return;
      }
      const response = await fetchQuery<unifiedIngestListTemplatesQuery>(
        environment,
        unifiedIngestListTransformTemplates,
        variables
      ).toPromise();
      setTemplateList(response);
    },
    [environment]
  );

  useEffect(() => {
    if (!domOwner) {
      return;
    }
    getTemplateList(
      {
        params: {
          owner_name: domOwner ?? '',
        },
      },
      setTemplateList
    );
  }, [domOwner, getTemplateList]);

  useEffect(() => {
    if (!(domOwner && organizationSlug && targetTableName)) {
      return;
    }
    getTemplateList(
      {
        params: {
          owner_name: domOwner ?? '',
          org_slug: organizationSlug,
          target_table: targetTableName,
        },
      },
      setTableTemplateList
    );
  }, [domOwner, organizationSlug, targetTableName, getTemplateList]);

  const autoCompleteOptions = useMemo(
    () =>
      templateList?.unified_ingest_transform_templates_list?.templates_list
        ?.filter((template) => template?.template_display_name)
        .map((template) => ({
          label: template?.template_display_name ?? '',
          value: template?.template_uuid ?? '',
        })) ?? [],
    [templateList]
  );

  const targetTableSelectedOptions = useMemo(
    () =>
      tableTemplateList?.unified_ingest_transform_templates_list?.templates_list?.map(
        (template) => ({
          label: template?.template_display_name ?? '',
          value: template?.template_uuid ?? '',
        })
      ) ?? [],
    [tableTemplateList]
  );

  const handleExtractionTemplateAutoCompleteChange = useCallback(
    async (
      newSelectedOptions: {
        label: string;
        value: string;
      }[]
    ) => {
      try {
        const templatesToAdd = newSelectedOptions.filter(
          (newOption) =>
            !targetTableSelectedOptions.some(
              (option) => option.label === newOption.label
            )
        );

        const templatesToRemove = targetTableSelectedOptions.filter(
          (existingOption) =>
            !newSelectedOptions.some(
              (option) => option.label === existingOption.label
            )
        );

        const addPromises = templatesToAdd.map((templateToAdd) => {
          if (domOwner && organizationSlug && targetTableName && id) {
            return duplicateTemplate({
              domOwner,
              templateUUID: templateToAdd.value,
              userId: id?.toString(),
              organizationSlug,
              targetTableName,
            });
          }
          return Promise.resolve();
        });

        const removePromises = templatesToRemove.map((templateToRemove) => {
          if (domOwner && organizationSlug && targetTableName && id) {
            return editTemplate({
              params: {
                owner_name: domOwner,
                template_uuid: templateToRemove.value,
                is_displayable: false,
              },
            });
          }
          return Promise.resolve();
        });

        const results = await Promise.allSettled([
          ...addPromises,
          ...removePromises,
        ]);

        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(
              `Template operation failed: ${
                index < templatesToAdd.length ? 'add' : 'remove'
              }`,
              result.reason
            );
          }
        });

        if (domOwner && organizationSlug && targetTableName) {
          await getTemplateList(
            {
              params: {
                owner_name: domOwner ?? '',
                org_slug: organizationSlug,
                target_table: targetTableName,
              },
            },
            setTableTemplateList
          );
        }
      } catch (error) {
        const errorMessage = 'Error updating templates.';
        context.showSnackbar({
          message: errorMessage,
          type: 'error',
        });
        console.error(errorMessage, error);
      }
    },
    [
      targetTableSelectedOptions,
      domOwner,
      organizationSlug,
      targetTableName,
      id,
      duplicateTemplate,
      editTemplate,
      getTemplateList,
    ]
  );

  return (
    <ModelLibrarySection
      title='Extraction Templates'
      description='Search and select which extraction templates are assigned to this target table.'
      autoCompleteOptions={autoCompleteOptions}
      selectedValues={targetTableSelectedOptions}
      onAutoCompleteChange={handleExtractionTemplateAutoCompleteChange}
      actionButton={
        <Button endIcon={<AddCircleIcon />} onClick={handleCreateClick}>
          Create
        </Button>
      }
    />
  );
};

interface TemplatesSectionWrapperProps {
  handleCreateClick?: () => void;
}
const TemplatesSectionWrapper: React.FC<TemplatesSectionWrapperProps> = ({
  handleCreateClick,
}) => {
  const { modelId, tableId } = useParams<{
    modelId: string;
    tableId: string;
  }>();
  const { targetTableDefinition } =
    useModelTableDetails(modelId, tableId) ?? {};
  const environment = useDspRelayEnvironment();

  if (!environment) {
    return null;
  }
  return (
    <RelayEnvironmentProvider environment={environment}>
      <RelayEnvironmentScope environment='view_admin'>
        <TemplatesSection
          targetTableName={targetTableDefinition?.name}
          handleCreateClick={handleCreateClick}
        />
      </RelayEnvironmentScope>
    </RelayEnvironmentProvider>
  );
};

TemplatesSectionWrapper.displayName = 'TemplatesSectionWrapper';
export default TemplatesSectionWrapper;
